/**
 * TariffEditor — editor pásmového tarifního schématu „od N. osoby“ (rozbalený řádek termínu, layout B).
 * Přepínač Sdílené / Vlastní a karty pásem (Osob + Cena v měně tarifu); poslední pásmo pokrývá zbytek do kapacity.
 * Prezentační komponenta – stav i změny drží orchestrátor (CalculationContent). Pouze Tailwind `phx-*` tokeny.
 */
import { Plus, Share2, SlidersVertical, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { cn } from '@/shared/lib/utils';
import { Button, Input, Panel, SegmentedButtons } from '@/shared/ui';
import type { TierDraft } from '../model/calc';

interface TariffEditorProps {
  scheme: 'shared' | 'own';
  onSchemeChange: (scheme: 'shared' | 'own') => void;
  tiers: TierDraft[];
  onTierChange: (index: number, patch: Partial<TierDraft>) => void;
  onAddTier: () => void;
  onRemoveTier: (index: number) => void;
  tariffCurrency: string;
  disabled?: boolean;
}

const capsLabel = 'text-phx-2xs font-semibold uppercase tracking-wide text-phx-text-muted';

function parseNumber(value: string): number {
  const parsed = Number.parseInt(value.replace(/[^\d]/g, ''), 10);
  return Number.isNaN(parsed) ? 0 : parsed;
}

export function TariffEditor({
  scheme,
  onSchemeChange,
  tiers,
  onTierChange,
  onAddTier,
  onRemoveTier,
  tariffCurrency,
  disabled = false,
}: TariffEditorProps) {
  const { t } = useTranslation('venua-planner');

  const note =
    scheme === 'own' ? t('calculation.tariff.ownNote') : t('calculation.tariff.sharedNote');

  let cumulative = 0;

  return (
    <div className="gap-phx-3 flex flex-col">
      <div className="gap-phx-3 flex flex-wrap items-center">
        <span className="text-phx-text-2 text-phx-2xs font-semibold tracking-wide uppercase">
          {t('calculation.tariff.schemeLabel')}
        </span>
        {/* Obal ikony a popisku musí být `flex`, ne `inline-flex`. SegmentedButtons vkládá `label`
            do Buttonu, který ho obalí spanem — inline-level box by se pak zarovnával přes
            `vertical-align: baseline`, a účaří `inline-flex` kontejneru je spodní hrana jeho první
            položky, tedy SVG. Obsah tlačítka tím vyskočil o 2 px nahoru. */}
        <SegmentedButtons<'shared' | 'own'>
          value={scheme}
          onChange={onSchemeChange}
          disabled={disabled}
          options={[
            {
              value: 'shared',
              label: (
                <span className="flex items-center gap-1.5">
                  <Share2 size={14} />
                  {t('calculation.tariff.shared')}
                </span>
              ),
            },
            {
              value: 'own',
              label: (
                <span className="flex items-center gap-1.5">
                  <SlidersVertical size={14} />
                  {t('calculation.tariff.own')}
                </span>
              ),
            },
          ]}
        />
        <span className="text-phx-text-muted text-phx-xs">{note}</span>
      </div>

      <div className="flex flex-wrap items-stretch gap-x-6 gap-y-3">
        {tiers.map((tier, index) => {
          const isLast = index === tiers.length - 1;
          const fromPerson = cumulative + 1;
          cumulative += tier.count;
          // Každý tarif lze smazat (kromě posledního zbývajícího pásma).
          const removable = tiers.length > 1;
          return (
            // Ghost panel pásma (layout B) přes DS komponentu Panel (variant ghost = průhledné pozadí + linka pod hlavičkou).
            <Panel
              key={index}
              variant="ghost"
              density="tight"
              className="w-39.5 [&_.phx-panel-head]:px-0"
              title={t('calculation.tariff.fromPerson', { n: fromPerson })}
              actions={
                removable ? (
                  <Button
                    size="icon-sm"
                    variant="ghost"
                    disabled={disabled}
                    title={t('calculation.tariff.removeTier')}
                    onClick={() => onRemoveTier(index)}
                  >
                    <X size={11} />
                  </Button>
                ) : undefined
              }
            >
              <div className="gap-phx-2 flex flex-col">
                <div className="flex items-center gap-2">
                  <span className={cn(capsLabel, 'shrink-0')}>
                    {t('calculation.tariff.persons')}
                  </span>
                  {isLast ? (
                    <span className="text-phx-text-muted h-phx-control text-phx-xs flex flex-1 items-center justify-end text-right italic">
                      {t('calculation.tariff.toCapacity')}
                    </span>
                  ) : (
                    <Input
                      size="sm"
                      type="number"
                      min={0}
                      className="flex-1"
                      unit={t('calculation.tariff.personsUnit')}
                      value={tier.count}
                      disabled={disabled}
                      onChange={(event) =>
                        onTierChange(index, { count: parseNumber(event.target.value) })
                      }
                    />
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <span className={cn(capsLabel, 'shrink-0')}>{t('calculation.tariff.price')}</span>
                  <Input
                    size="sm"
                    type="number"
                    min={0}
                    step={100}
                    className="flex-1"
                    unit={tariffCurrency}
                    value={tier.price}
                    disabled={disabled}
                    onChange={(event) =>
                      onTierChange(index, { price: parseNumber(event.target.value) })
                    }
                  />
                </div>
              </div>
            </Panel>
          );
        })}

        <button
          type="button"
          disabled={disabled}
          onClick={onAddTier}
          className="border-phx-border-3 text-phx-text-muted hover:border-phx-primary hover:text-phx-primary hover:bg-phx-primary-tint rounded-phx text-phx-xs flex w-[120px] cursor-pointer flex-col items-center justify-center gap-1.5 border border-dashed bg-transparent font-medium transition-colors disabled:cursor-default disabled:opacity-50"
        >
          <Plus size={18} />
          {t('calculation.tariff.addTier')}
        </button>
      </div>
    </div>
  );
}
